"use client"

import React from "react"
import { User, PublicUser } from "@/types/users"
import { AuthorAvatar } from "@/app/components/Avatar"
import ClickableHoverCard from "./ClickableHoverCard"
import TruncatedText from "@/app/components/TruncatedText"
import Image from "next/image"
import Farcaster from "public/publication/farcaster.png"
import { cn } from "@/lib/utils"

interface ClickableAuthorHoverCardProps {
  author: Partial<User> | PublicUser
  className?: string
  avatarSize?: number
  showFarcasterIcon?: boolean
  avatarClassName?: string
  children: React.ReactNode
}

/**
 * A reusable component that displays an author avatar with a hover card
 * containing their bio and social links.
 * Used in both AuthorHoverCard and CommentAuthorHoverCard components.
 */
export default function ClickableAuthorHoverCard({
  author,
  className = "",
  avatarSize = 40,
  showFarcasterIcon = false,
  avatarClassName = "h-10 w-10 dark:border-none ring ring-background",
  children,
}: ClickableAuthorHoverCardProps) {
  return (
    <ClickableHoverCard
      className={className}
      trigger={
        <div className="cursor-pointer relative">
          <AuthorAvatar
            user={author}
            size={avatarSize}
            className={cn(avatarClassName)}
          />

          {showFarcasterIcon && (
            <Image
              width={20}
              height={20}
              className="absolute -right-1 -bottom-1"
              src={Farcaster}
              alt="Farcaster"
            />
          )}
        </div>
      }
    >
      <div>
        <p className="text-sm font-semibold mb-2 text-foreground">
          {"displayName" in author && author.displayName
            ? author.displayName.name
            : author.authorName}
        </p>
        {author.authorBio && (
          <TruncatedText
            maxLength={120}
            text={author.authorBio}
            className="text-sm font-normal text-muted-foreground"
          />
        )}
      </div>
      {children}
    </ClickableHoverCard>
  )
}
